/**
 * useAuth Hook
 *
 * Manages authentication state using React Query.
 * Supports multi-company authentication with access/refresh tokens.
 * Supports multi-account and company switching.
 */

import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  authApi,
  twoFactorApi,
  isTwoFactorChallenge,
  type LoginResponse,
  type SessionPayload,
  type User,
  type Company,
  type AccessibleCompany,
} from '../services/api/auth';
import { accountManager, type AccountSession } from '../utils/accountManager';
import { startSessionRefresh } from '../utils/sessionRefresh';

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthState {
  user: User | null;
  company: Company | null;
  accessibleCompanies: AccessibleCompany[];
  activeCompanyId: string | null;
  allAccounts: Array<{ email: string; user: User }>;
}

/**
 * Sessions (by access token) already revalidated in this page load. Dozens of
 * components call useAuth(), and a per-instance ref meant each one re-fetched
 * /api/auth/company and /api/auth/me on mount. A login or company switch
 * mints a new token, which re-arms the check for that session.
 */
const revalidatedSessions = new Set<string>();

/** Test seam — forget which sessions were revalidated. */
export function resetAuthRevalidationForTests(): void {
  revalidatedSessions.clear();
}

export function useAuth() {
  const queryClient = useQueryClient();

  // Helper to load all accounts list
  const loadAllAccounts = (): Array<{ email: string; user: User }> => {
    const accountsList = accountManager.getAllAccountsList();
    return accountsList.map(({ email, session }) => ({
      email,
      user: session.user,
    }));
  };

  /*
    Whether the active company could be resolved, and a way to ask again.

    A failed `/api/auth/company` used to be indistinguishable from "no company",
    and the revalidation latch below made it permanent for the life of the
    token — so one rate-limited call left the session believing it was entitled
    to nothing, and waiting did not help (QA ledger AUTH2-001).
  */
  const [companyResolutionFailed, setCompanyResolutionFailed] = useState(false);
  const [resolutionAttempt, setResolutionAttempt] = useState(0);
  const retryCompanyResolution = useCallback(() => {
    const session = accountManager.getActiveSession();
    if (session) revalidatedSessions.delete(session.accessToken);
    setCompanyResolutionFailed(false);
    setResolutionAttempt((n) => n + 1);
  }, []);

  // Check if user is authenticated (from accountManager)
  /*
    eslint-disable-next-line @lognai/require-query-error-state --
    This query cannot fail into an error state: its queryFn resolves rather
    than throws, and a company that will not resolve is reported through
    `companyResolutionFailed`, which App.tsx gates on (QA ledger VO2-001).
    `isError` would be permanently false, so destructuring it would be
    misleading rather than safer.
  */
  const { data: authState, isLoading } = useQuery<AuthState>({
    queryKey: ['auth', 'state'],
    queryFn: async () => {
      const session = accountManager.getActiveSession();

      if (!session) {
        return {
          user: null,
          company: null,
          accessibleCompanies: [],
          activeCompanyId: null,
          allAccounts: [],
        };
      }

      return {
        user: session.user,
        company: session.activeCompany,
        accessibleCompanies: session.accessibleCompanies || [],
        activeCompanyId: session.activeCompanyId,
        allAccounts: loadAllAccounts(),
      };
    },
    staleTime: Infinity, // Auth data doesn't change often
  });

  // The session's `activeCompany` (and its `type`, which drives internal vs.
  // partner nav) is only ever fetched at login/switch-company time, then
  // cached indefinitely above — so a company record changed after login
  // (e.g. an admin correcting its type) never reaches an already-logged-in
  // tab. `enabledPlugins`/`effectivePlugins` ride on that same snapshot and
  // `App.tsx` feeds them straight to `loadPluginsForUser`, so a plugin toggled
  // for a company never took effect for an already-signed-in user either.
  // Revalidate once per mount and patch the cached session if it drifted.
  useEffect(() => {
    if (!authState?.user) return;
    (async () => {
      const session = accountManager.getActiveSession();
      if (!session || revalidatedSessions.has(session.accessToken)) return;
      /*
        Claimed BEFORE the awaits, so the dozen components that mount useAuth()
        on one page load make one request between them rather than a dozen
        (QA ledger PROC-003). Released again below if the company could not be
        resolved — de-duplicating concurrent callers must not also mean latching
        a failure in for the life of the token, which is what made a single
        rate-limited call permanent (QA ledger AUTH2-001).
      */
      revalidatedSessions.add(session.accessToken);
      // `user.permissions` is the same kind of snapshot: taken from the login /
      // switch-company body. Since 2026-09 the server resolves permissions per
      // request (they are no longer in the token), so a role change bites on
      // the API within seconds — pull the fresh set here so the sidebar and
      // page gates catch up on reload instead of at the next login. A failed
      // /me still fails soft — it resolves to null and keeps the snapshot —
      // because permissions drifting by one reload is survivable. The COMPANY
      // is not: it decides which modules exist at all, so it is retried below.
      /*
        Retried, because the answer decides what the whole app believes this
        account may reach. A single rate-limited call used to leave the session
        entitled to nothing until the token changed — the latch below is now
        only set once the company has actually been resolved.

        A platform admin resolves to `{ resolved: true, company: null }` and
        takes the success path on the first attempt.
      */
      let companyResult = await authApi.getCurrentCompanyResult();
      for (let attempt = 1; attempt < 3 && !companyResult.resolved; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, attempt * 600));
        companyResult = await authApi.getCurrentCompanyResult();
      }

      if (!companyResult.resolved) {
        // Release the claim so a later mount, or the retry button, can ask
        // again. Better a bounded "we could not load your workspace" than a
        // shell that quietly claims the modules do not exist.
        revalidatedSessions.delete(session.accessToken);
        setCompanyResolutionFailed(true);
        return;
      }
      setCompanyResolutionFailed(false);

      const fresh = companyResult.company;
      const freshUser = await authApi.getCurrentUser();
      const freshPermissions =
        freshUser?.permissions &&
        JSON.stringify(freshUser.permissions) !== JSON.stringify(session.user.permissions)
          ? freshUser.permissions
          : undefined;
      const company = fresh ?? session.activeCompany;
      const stale = session.activeCompany;
      const companyChanged =
        !!fresh &&
        (!stale ||
          stale.type !== fresh.type ||
          stale.name !== fresh.name ||
          JSON.stringify(stale.effectivePlugins) !== JSON.stringify(fresh.effectivePlugins) ||
          JSON.stringify(stale.enabledPlugins) !== JSON.stringify(fresh.enabledPlugins) ||
          // `settings` carries the company's default currency, which plugins read
          // off this cached snapshot to prefill forms. Without it here, an admin
          // changing the default never reached an already-signed-in tab.
          JSON.stringify(stale.settings) !== JSON.stringify(fresh.settings) ||
          // Brand assets white-label the shell off this same snapshot; a
          // platform admin replacing a logo must reach an open tab on reload.
          JSON.stringify(stale.branding) !== JSON.stringify(fresh.branding));
      if (!companyChanged && !freshPermissions) return;
      accountManager.updateActiveCompany(
        session.activeCompanyId,
        session.accessToken,
        session.refreshToken,
        session.expiresAt,
        company,
        freshPermissions
      );
      queryClient.setQueryData(['auth', 'state'], (prev: AuthState | undefined) => ({
        ...(prev as AuthState),
        company,
        user: freshPermissions && prev?.user ? { ...prev.user, permissions: freshPermissions } : prev?.user ?? null,
      }));
    })();
  }, [authState?.user, queryClient, resolutionAttempt]);

  /*
    Keep the access token fresh for as long as the user is signed in.

    The token is deliberately short-lived now (AUTH2-003): it is stored in
    localStorage, so the thing that matters is how long a stolen one stays
    useful. That is only affordable because something renews it — nothing did
    before, and a 401 simply ended the session.
  */
  useEffect(() => {
    if (!authState?.user) return;
    return startSessionRefresh();
  }, [authState?.user]);

  // Login mutation
  /**
   * Turn a completed sign-in into the active session.
   *
   * Shared by password-only login and the two-factor challenge, because the
   * two produce the same thing. Duplicating it is how the second path would
   * quietly skip a step — the accessible-companies fetch, say — and leave a
   * user signed in with half a session.
   */
  const establishSession = async (payload: SessionPayload): Promise<void> => {
    const { accessToken, refreshToken, expiresIn, user } = payload;
    const expiresAt = Date.now() + expiresIn * 1000;
    const companyId = user.companyId || '';

    // Create account session (company will be populated after getCurrentCompany call)
    const session: AccountSession = {
      accessToken,
      refreshToken,
      expiresAt,
      user,
      activeCompanyId: companyId,
      activeCompany: null,
      accessibleCompanies: [],
    };

    // Save to accountManager
    accountManager.setAccount(user.email, session);
    accountManager.setActiveAccount(user.email);

    /*
      Fetch accessible companies and the current company (with enabledPlugins).

      The company is retried: a single rate-limited call here used to persist a
      session whose `activeCompany` was null, which every later page load read
      as "entitled to nothing" — sidebar pruned, plugin routes 404 — and the
      once-per-token revalidation latch meant it never recovered
      (QA ledger AUTH2-001).
    */
    const [companiesResponse, companyResult] = await Promise.all([
      authApi.getAccessibleCompanies(),
      (async () => {
        let result = await authApi.getCurrentCompanyResult();
        for (let attempt = 1; attempt < 3 && !result.resolved; attempt += 1) {
          await new Promise((resolve) => setTimeout(resolve, attempt * 600));
          result = await authApi.getCurrentCompanyResult();
        }
        return result;
      })(),
    ]);
    const companyData = companyResult.resolved ? companyResult.company : null;
    /*
      Claim the revalidation latch only when the company actually resolved:
      login has just done that work, so the effect need not repeat it. If it did
      NOT resolve, the latch stays open so the effect retries instead of the
      session settling into "entitled to nothing" (QA ledger AUTH2-001).
    */
    if (companyResult.resolved) {
      revalidatedSessions.add(accessToken);
    }

    if (companiesResponse.success && companiesResponse.data) {
      accountManager.updateAccessibleCompanies(companiesResponse.data.companies);
    }

    // Update with full company data (includes enabledPlugins)
    if (companyData) {
      const fullCompanyId = companyData.id || user.companyId || '';
      accountManager.updateActiveCompany(
        fullCompanyId,
        accessToken,
        refreshToken,
        expiresAt,
        companyData
      );
    }

    // Update React Query cache with fetched company data
    const updatedSession = accountManager.getActiveSession();
    queryClient.setQueryData(['auth', 'state'], {
      user,
      company: companyData || null,
      accessibleCompanies: updatedSession?.accessibleCompanies || [],
      activeCompanyId: companyData?.id || companyId,
      allAccounts: loadAllAccounts(),
    });
  };

  const loginMutation = useMutation<LoginResponse, Error, LoginCredentials>({
    mutationFn: (credentials) => authApi.login(credentials),
    onSuccess: async (response) => {
      // A two-factor challenge carries no tokens. Falling through would write
      // a session with `accessToken: undefined` and sign the user into nothing.
      if (!response.success || !response.data || isTwoFactorChallenge(response.data)) return;
      await establishSession(response.data);
    },
  });

  /** The second half of a sign-in: a code against the ticket login handed back. */
  const twoFactorMutation = useMutation<
    LoginResponse,
    Error,
    { challengeToken: string; code: string }
  >({
    mutationFn: ({ challengeToken, code }) => twoFactorApi.challenge(challengeToken, code),
    onSuccess: async (response) => {
      if (!response.success || !response.data || isTwoFactorChallenge(response.data)) return;
      await establishSession(response.data);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      await authApi.logout(accountManager.getActiveSession() ?? undefined);
    },
    onSuccess: () => {
      const email = accountManager.getActiveAccountEmail();
      if (email) {
        accountManager.removeAccount(email);
      }

      // Check if there are other accounts
      const remainingAccounts = accountManager.getAllAccountsList();
      if (remainingAccounts.length > 0) {
        window.location.reload();
      } else {
        queryClient.setQueryData(['auth', 'state'], {
          user: null,
          company: null,
          accessibleCompanies: [],
          activeCompanyId: null,
          allAccounts: [],
        });
        queryClient.clear();
      }
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation<
    { success: boolean; error?: string },
    Error,
    { currentPassword: string; newPassword: string }
  >({
    mutationFn: ({ currentPassword, newPassword }) =>
      authApi.changePassword(currentPassword, newPassword),
  });

  // Forgot password mutation (sends reset link to email)
  const forgotPasswordMutation = useMutation<
    { success: boolean; error?: string },
    Error,
    { email: string }
  >({
    mutationFn: ({ email }) => authApi.forgotPassword(email),
  });

  // Reset password mutation (uses token from reset link)
  const resetPasswordMutation = useMutation<
    { success: boolean; error?: string },
    Error,
    { token: string; newPassword: string }
  >({
    mutationFn: ({ token, newPassword }) =>
      authApi.resetPassword(token, newPassword),
  });

  // Switch company mutation
  const switchCompanyMutation = useMutation({
    mutationFn: async (companyId: string) => {
      const response = await authApi.switchCompany(companyId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to switch company');
      }
      return response;
    },
    onSuccess: (response) => {
      if (response.success && response.data) {
        const { accessToken, refreshToken, expiresIn, company, user: switchedUser } = response.data;
        const expiresAt = Date.now() + expiresIn * 1000;

        accountManager.updateActiveCompany(
          company.id,
          accessToken,
          refreshToken,
          expiresAt,
          company,
          switchedUser.permissions
        );

        // Reload page for clean state
        window.location.reload();
      }
    },
  });

  const user = authState?.user ?? null;
  const company = authState?.company ?? null;
  const accessibleCompanies = authState?.accessibleCompanies ?? [];
  const activeCompanyId = authState?.activeCompanyId ?? null;
  const allAccounts = authState?.allAccounts ?? [];
  const isPlatformAdmin = user?.roles?.includes('platform_admin') ?? false;

  const switchAccount = async (email: string) => {
    accountManager.setActiveAccount(email);

    // Return the account to its PRIMARY company, so switching to it behaves like
    // a fresh login (which is what works standalone/incognito). The stored
    // session may be parked in a previously company-switched secondary company,
    // where the account has reduced permissions — which otherwise surfaces as
    // "permission denied" errors even though the account is perfectly valid.
    const session = accountManager.getActiveSession();
    const primaryCompanyId = session?.user?.companyId;
    if (primaryCompanyId && session && session.activeCompanyId !== primaryCompanyId) {
      try {
        const response = await authApi.switchCompany(primaryCompanyId);
        if (response.success && response.data) {
          const { accessToken, refreshToken, expiresIn, company, user: switchedUser } = response.data;
          accountManager.updateActiveCompany(
            company.id,
            accessToken,
            refreshToken,
            Date.now() + expiresIn * 1000,
            company,
            switchedUser.permissions
          );
        }
      } catch {
        // Best-effort: if the reset fails the account still loads at its stored
        // company; the user can switch company manually.
      }
    }

    // Land on the default route (like a fresh login) rather than reloading the
    // current route, which the switched account may not have permission for.
    window.location.href = '/workspace';
  };

  const addAccount = () => {
    window.location.href = '/login?mode=addAccount';
  };

  const logoutAccount = async (email: string) => {
    // Revoke THAT account's tokens, not the active account's.
    await authApi.logout(accountManager.getAllAccounts()[email]);
    accountManager.removeAccount(email);

    const activeEmail = accountManager.getActiveAccountEmail();
    if (!activeEmail || email === user?.email) {
      const remainingAccounts = accountManager.getAllAccountsList();
      if (remainingAccounts.length > 0) {
        window.location.reload();
      } else {
        queryClient.setQueryData(['auth', 'state'], {
          user: null,
          company: null,
          accessibleCompanies: [],
          activeCompanyId: null,
          allAccounts: [],
        });
        window.location.href = '/login';
      }
    } else {
      // Refresh accounts list
      queryClient.invalidateQueries({ queryKey: ['auth', 'state'] });
    }
  };

  const logoutAllAccounts = async () => {
    // Every account is its own session on the server; revoke each.
    await Promise.all(
      accountManager.getAllAccountsList().map(({ session }) => authApi.logout(session))
    );
    accountManager.clearAll();
    queryClient.setQueryData(['auth', 'state'], {
      user: null,
      company: null,
      accessibleCompanies: [],
      activeCompanyId: null,
      allAccounts: [],
    });
    queryClient.clear();
    window.location.href = '/login';
  };

  return {
    user,
    company,
    isLoading,
    isAuthenticated: !!user,
    isPlatformAdmin,
    // Multi-account support
    accessibleCompanies,
    activeCompanyId,
    allAccounts,
    // Actions
    login: loginMutation.mutateAsync,
    logout: logoutMutation.mutateAsync,
    changePassword: changePasswordMutation.mutateAsync,
    forgotPassword: forgotPasswordMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutateAsync,
    isLoggingIn: loginMutation.isPending,
    loginError: loginMutation.error,
    // Whether the active company could be resolved at all, and a way to ask
    // again — see the retry in the revalidation effect (QA ledger AUTH2-001).
    companyResolutionFailed,
    retryCompanyResolution,
    // Two-factor: the second half of a sign-in.
    completeTwoFactor: twoFactorMutation.mutateAsync,
    isVerifyingTwoFactor: twoFactorMutation.isPending,
    isForgotPasswordPending: forgotPasswordMutation.isPending,
    isResetPasswordPending: resetPasswordMutation.isPending,
    // Multi-account actions
    switchCompany: switchCompanyMutation.mutateAsync,
    switchAccount,
    addAccount,
    logoutAccount,
    logoutAllAccounts,
  };
}

export default useAuth;
